import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { resolveImage } from "@/lib/api";
import { usePageTitle } from "@/contexts/PageTitlesContext";
import { ArrowLeft } from "lucide-react";

export function CrystalsList() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const pt = usePageTitle("crystals", { title_zh: t("crystals.title"), title_en: t("crystals.title"), subtitle_zh: t("crystals.subtitle"), subtitle_en: t("crystals.subtitle") });
  const [articles, setArticles] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("");

  useEffect(() => {
    api.get("/article-categories").then((r) => setCats(r.data));
    load("");
  }, []);

  const load = (c) => {
    api.get("/articles", { params: c ? { category: c } : {} }).then((r) => setArticles(r.data));
  };
  const setCat = (c) => { setActive(c); load(c); };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid="crystals-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold" data-testid="crystals-subtitle">{zh ? pt.subtitle_zh : pt.subtitle_en}</div>
      <h1 className="mt-3 font-serif-cn text-4xl md:text-5xl text-[#003645] font-semibold tracking-tight" data-testid="crystals-title">
        {zh ? pt.title_zh : pt.title_en}
      </h1>

      <div className="mt-10 flex flex-wrap gap-2" data-testid="article-cats">
        <button onClick={() => setCat("")} className={`px-4 py-1.5 rounded-full text-sm font-medium border ${!active ? "bg-[#005870] text-white border-[#005870]" : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"}`} data-testid="cat-article-all">
          {t("common.all")}
        </button>
        {cats.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} className={`px-4 py-1.5 rounded-full text-sm font-medium border ${active === c.key ? "bg-[#005870] text-white border-[#005870]" : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"}`} data-testid={`cat-article-${c.key}`}>
            {zh ? c.name_zh : c.name_en}
          </button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="articles-list">
        {articles.length === 0 ? (
          <div className="text-[#4a6e77]">{t("crystals.empty")}</div>
        ) : articles.map((a) => (
          <Link key={a.id} to={`/crystals/${a.id}`} className="group block" data-testid={`article-card-${a.id}`}>
            {a.cover_image && (
              <div className="aspect-[4/3] overflow-hidden rounded-md bg-[#f5f0e8]">
                <img src={resolveImage(a.cover_image)} alt="" className="card-img h-full w-full object-cover" />
              </div>
            )}
            <div className="mt-4">
              <div className="font-serif-cn text-lg text-[#003645] font-medium group-hover:text-[#00868e] transition-colors duration-200">
                {zh ? a.title_zh : a.title_en}
              </div>
              <div className="mt-2 text-sm text-[#4a6e77] line-clamp-3">
                {(zh ? a.body_zh : a.body_en).slice(0, 120)}
              </div>
              <div className="mt-3 text-xs uppercase tracking-widest text-[#00868e] font-semibold">
                {t("crystals.readMore")} →
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function CrystalArticle() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [article, setArticle] = useState(null);

  useEffect(() => {
    api.get(`/articles/${id}`).then((r) => setArticle(r.data));
  }, [id]);

  if (!article) return <div className="p-16 text-[#4a6e77]">{t("common.loading")}</div>;

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16" data-testid="article-page">
      <Link to="/crystals" className="text-sm text-[#4a6e77] hover:text-[#005870] inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> {t("common.back")}
      </Link>
      <h1 className="mt-6 font-serif-cn text-4xl text-[#003645] font-semibold leading-tight">
        {zh ? article.title_zh : article.title_en}
      </h1>
      {article.cover_image && (
        <div className="mt-8 aspect-[16/9] rounded-md overflow-hidden bg-[#f5f0e8]">
          <img src={resolveImage(article.cover_image)} alt="" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="mt-10 font-serif-cn text-[17px] leading-[2] text-[#4a6e77] whitespace-pre-wrap">
        {zh ? article.body_zh : article.body_en}
      </div>
    </div>
  );
}
